import { useCallback, useState } from 'react';
import type { z } from 'zod';
import { fieldErrors } from './resources';

/**
 * Minimal form state: values, field errors from Zod (client) or the API (422), and a submit helper.
 */
export function useFormState<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => {
      if (!e[key as string]) return e;
      const { [key as string]: _, ...rest } = e;
      return rest;
    });
  }, []);

  /**
   * Validates with `schema`, then runs `save`; maps API field errors back onto the form. `data`
   * replaces the form values when some of them must not be sent.
   */
  async function submit<S extends z.ZodTypeAny>(
    schema: S,
    save: (data: z.output<S>) => Promise<unknown>,
    data: unknown = values,
  ) {
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0] ?? '_')] ??= issue.message;
      setErrors(next);
      return false;
    }
    try {
      await save(parsed.data);
      return true;
    } catch (err) {
      const fields = fieldErrors(err);
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(fields)) next[k.split('.')[0]!] ??= v;
      setErrors(next);
      return false;
    }
  }

  return { values, setValues, set, errors, setErrors, submit };
}
